/**
 * The study export projection (PRD H2; increment-9 plan §1 PR4).
 *
 * PURE: this module never touches a database. The api-side export service
 * gathers the blessed reads (plan §0 rule 10 — `confirmed_session_records`
 * and the sealed/filed reads the dashboards already use) and hands them here;
 * this module PROJECTS them into the Patient-001 dataset's nine-key shape
 * (the adopted contract, register Cluster H v3 rescope) and applies the
 * redaction calls of record (plan §0.1 calls 4/5, DISCREPANCIES-V2 §8).
 *
 * REDACTION IS STRUCTURAL, NEVER SCRUBBING (§0 rule 9). The projection only
 * ever copies fields this file names; the patient's name, third-party names
 * (`supportPerson`, `surgeon`, `facility`) and clinician user ids have no
 * path into the output — the patient block is built from an explicit
 * allowlist, identity is the stored study code (delta 2), and every actor
 * passes through `pseudonymOf`, which THROWS on an unmapped actor rather
 * than letting a real id fall through.
 *
 * FREE TEXT IS EXCLUDED BY DEFAULT (call 4): every physio-authored sentence
 * — session notes, observation notes, tweak rationales, outcome reasons,
 * early-stop reasons, clinical summaries, TUG/SLR notes, flag notes/actions/
 * resolutions, the enrolment's typed `diagnosis` and `procedure` (AUD-16),
 * and any STRING confirmed value (a 500-char string field is prose as far as
 * redaction is concerned) — flows through ONE gate, `gateFreeText`. The
 * `full-text-unscreened` profile is the explicit choice;
 * the route that offers it (PR6) records the choice in that export's
 * access_log row. **That profile's output is not a redacted file and does not
 * claim to be** — see the note on STUDY_EXPORT_PROFILES for why the name
 * matters more than it looks.
 *
 * ABSENCE RENDERS AS ABSENT, NEVER AS ZERO (delta 4, D4/F18 one layer out):
 * an unsigned session contributes a bare status and nothing else; a skipped
 * observation is null; an undefined ratio is null. The dataset is a complete
 * synthetic episode — a real one is not, and the export says so.
 *
 * CLINICAL CONTENT ARRIVES AS DATA. Protocol prose, exercise identities,
 * flag labels and WOMAC labels all come in through the inputs (the pinned
 * template, the stored records); nothing clinical is authored here — the
 * clinical-isolation guard holds this file to that.
 */

import { z } from 'zod';

import type { AssessmentMissedEvent, AssessmentRecord } from './assessment';
import type { FlagEventRecord } from './flags';
import type { Episode, Patient } from './episode';
import type { SessionHistoryRow } from './sessionHistory';
import type { HeatmapCell, ComplianceRollup, ObservationDailyPoint } from './rollup';
import type { SessionExerciseOutcome, SignOff } from './confirmation';
import type { SessionObservation } from './assessment';

// ---- profiles (call 4) ------------------------------------------------------

/**
 * The two profiles, named for what the FILE is rather than for what the
 * pipeline did to it.
 *
 * ⚠ `full-text-unscreened` deliberately makes NO redaction claim, and the
 * name is a decision of record (owner, 2026-08-07, on review of PR4's first
 * sample export). It was `pii-redacted-with-free-text` for exactly as long as
 * it took to generate a real episode and search it: the physio's own session
 * note read *"Sunita was anxious about the swelling; … booked a review call
 * with Dr. Test-Kulkarni for Thursday"* — the patient's first name and the
 * surgeon's, inside prose, in a file whose name promised redaction. Call 4
 * had already ruled on this in advance and both halves of its sentence bind:
 * free text is includable by an explicit choice, AND **"PII-redacted" must
 * not appear on a file containing sentences nobody screened.**
 *
 * The structural redaction is identical under both profiles — the patient
 * block is the same allowlist, names never travel as FIELDS, clinicians are
 * pseudonyms. The difference is prose, and prose is unscreenable by rule.
 */
export const STUDY_EXPORT_PROFILES = ['pii-redacted', 'full-text-unscreened'] as const;
export const studyExportProfileSchema = z.enum(STUDY_EXPORT_PROFILES);
export type StudyExportProfile = z.infer<typeof studyExportProfileSchema>;

/** The one free-text gate. Everything prose-capable passes through here. */
export function gateFreeText(
  profile: StudyExportProfile,
  value: string | null | undefined,
): string | null {
  if (value == null) return null;
  return profile === 'full-text-unscreened' ? value : null;
}

// ---- clinician pseudonyms (call 5) -------------------------------------------

/**
 * Stable pseudonyms in FIRST-APPEARANCE ORDER over the assembled record.
 * Stability holds because the record is append-only: a re-export can only
 * append actors after the ones already seen, never reorder them.
 */
export function buildClinicianPseudonyms(
  orderedActors: readonly string[],
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const actor of orderedActors) {
    if (!map.has(actor)) {
      // A–Z then AA, AB… — the cohort will never see it, but never wrap.
      const n = map.size;
      const letters =
        n < 26
          ? String.fromCharCode(65 + n)
          : String.fromCharCode(64 + Math.floor(n / 26)) + String.fromCharCode(65 + (n % 26));
      map.set(actor, `Clinician-${letters}`);
    }
  }
  return map;
}

/** Fail-closed: an actor the map has never seen must never pass through raw. */
export function pseudonymOf(map: ReadonlyMap<string, string>, actor: string): string {
  const pseudonym = map.get(actor);
  if (!pseudonym) throw new Error('study export: unmapped clinician actor');
  return pseudonym;
}

// ---- input shapes (what the api service assembles from blessed reads) --------

/** The pinned template, structurally (protocol builds ON shared, so this is a mirror, not an import). */
export interface StudyExportTemplate {
  id: string;
  version: string;
  title: string;
  status: string;
  source: string;
  weeks: number;
  daysPerWeek: number;
  exercises: ReadonlyArray<{
    code: string;
    name: string;
    focus: string;
    movement: string;
    sets: number;
    reps: number;
  }>;
  phases: ReadonlyArray<{
    number: number;
    name: string;
    goal: string;
    fromWeek: number;
    toWeek: number;
  }>;
  holdProgression: ReadonlyArray<{ week: number; holdSecondsMin: number; holdSecondsMax: number }>;
  holdApplications: ReadonlyArray<{ exerciseCode: string; application: string }>;
  /** "Measurement & Validity Rules", verbatim bullets. */
  validityRules: readonly string[];
  /** "Safety boundaries"; `kind` is the template's word, exported as `type`. */
  safetyBoundaries: ReadonlyArray<{ rule: string; kind: 'stop' | 'technique' | 'escalate' }>;
  assessments: ReadonlyArray<{
    day: number;
    timepoint: string;
    instruments: readonly string[];
    targetText: string | null;
  }>;
}

/** One flag definition, passed as data (labels are protocol-authored). */
export interface StudyExportFlagDefinition {
  code: string;
  label: string;
  ruleText: string;
  severity: string;
  mode: string;
  thresholdsProposed: boolean;
}

export interface StudyExportArtifact {
  kind: string;
  partNumber: number;
  contentType: string;
  uploadState: string;
  sizeBytes: number | null;
  contentHash: string | null;
  objectKey: string;
  createdAt: string;
}

export interface SealedSessionDetail {
  signOff: SignOff;
  outcomes: readonly SessionExerciseOutcome[];
  observation: SessionObservation | null;
  tweaks: ReadonlyArray<{
    exerciseCode: string;
    paused: boolean;
    sets: number | null;
    reps: number | null;
    rationale: string;
  }>;
  artifacts: readonly StudyExportArtifact[];
  /** Earliest 'live' / earliest 'ended' state events (the retention anchor's own facts). */
  startedAt: string | null;
  endedAt: string | null;
}

export interface StudyExportInputs {
  profile: StudyExportProfile;
  /** Clock instant of the export act — passed in, never read here (pure). */
  exportedAt: string;
  /** The rollup's own IST derivation date; every elapsed/`today` verdict is its. */
  derivedAgainstDate: string;
  episode: Episode;
  patient: Patient;
  /** The stored Patient-NNN (017). The assembly REFUSES without one — identity is the code. */
  studyCode: string;
  template: StudyExportTemplate;
  flagDefinitions: readonly StudyExportFlagDefinition[];
  compliance: ComplianceRollup;
  cells: readonly HeatmapCell[];
  observationDaily: readonly ObservationDailyPoint[];
  sealedSessions: readonly SessionHistoryRow[];
  sealedDetails: Readonly<Record<string, SealedSessionDetail>>;
  assessments: readonly AssessmentRecord[];
  assessmentMissedEvents: readonly AssessmentMissedEvent[];
  flags: readonly FlagEventRecord[];
  consentDocumentArtifacts: readonly StudyExportArtifact[];
}

// ---- small pure helpers -------------------------------------------------------

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function weekdayOf(isoDate: string): string {
  return WEEKDAYS[new Date(`${isoDate}T00:00:00Z`).getUTCDay()]!;
}

/** IST is a fixed +05:30 (no DST) — the project's clinical clock (istToday). */
const IST_OFFSET_MS = 330 * 60_000;

function istDateOf(isoInstant: string): string {
  return new Date(new Date(isoInstant).getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

function istClockOf(isoInstant: string): string {
  return new Date(new Date(isoInstant).getTime() + IST_OFFSET_MS).toISOString().slice(11, 16);
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** A confirmed value for the additive record: strings are prose-capable → gated. */
function gateConfirmedScalar(
  profile: StudyExportProfile,
  value: number | string | boolean,
): number | string | boolean | null {
  return typeof value === 'string' ? gateFreeText(profile, value) : value;
}

function numberOrNull(v: number | string | boolean | undefined): number | null {
  return typeof v === 'number' ? v : null;
}

// ---- the projection -----------------------------------------------------------

export function assembleStudyExport(inputs: StudyExportInputs): StudyExport {
  const { profile, template, compliance, cells } = inputs;
  // DERIVED FROM THE GATE ITSELF, never a second reading of the profile name.
  // This line was a restatement (`profile === '…-with-free-text'`) and the
  // profile rename left it stale in place: the gate kept letting prose
  // through while the file's own cover note said free text was excluded — a
  // document lying about itself, which is the one failure mode this whole
  // module exists to prevent. Ask the gate what it does; do not re-derive it.
  const freeTextIncluded = gateFreeText(profile, 'probe') !== null;

  // -- pseudonyms: first appearance over the record, in a fixed traversal ----
  const actors: string[] = [inputs.episode.createdBy];
  const bySealedAt = [...inputs.sealedSessions].sort((a, b) =>
    a.sealedAt.localeCompare(b.sealedAt),
  );
  for (const row of bySealedAt) {
    const detail = inputs.sealedDetails[row.sessionId];
    if (!detail) continue;
    actors.push(detail.signOff.signedOffBy);
    for (const o of detail.outcomes) actors.push(o.actor);
    if (detail.observation) actors.push(detail.observation.actor);
  }
  for (const r of [...inputs.assessments].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)))
    actors.push(r.actor);
  for (const m of inputs.assessmentMissedEvents) actors.push(m.actor);
  for (const f of inputs.flags) {
    actors.push(f.actor);
    if (f.resolution) actors.push(f.resolution.actor);
  }
  const pseudonyms = buildClinicianPseudonyms(actors);
  const who = (actor: string) => pseudonymOf(pseudonyms, actor);

  const gate = (value: string | null | undefined) => gateFreeText(profile, value);

  // -- template lookups -------------------------------------------------------
  const exerciseByCode = new Map(template.exercises.map((e) => [e.code, e]));
  const assessmentByDay = new Map(template.assessments.map((a) => [a.day, a]));
  const holdByWeek = new Map(template.holdProgression.map((h) => [h.week, h]));
  const rhythmic = new Set(
    template.holdApplications
      .filter((h) => h.application === 'rhythmic-no-hold')
      .map((h) => h.exerciseCode),
  );
  const phaseOfWeek = (week: number) =>
    template.phases.find((p) => week >= p.fromWeek && week <= p.toWeek)?.number ?? null;
  const holdSecondsOf = (week: number, exerciseCode: string | null): number | null => {
    const rule = holdByWeek.get(week);
    if (!rule) return null;
    if (exerciseCode !== null && rhythmic.has(exerciseCode)) return null;
    return rule.holdSecondsMin === rule.holdSecondsMax ? rule.holdSecondsMin : null;
  };

  const flagDefByCode = new Map(inputs.flagDefinitions.map((d) => [d.code, d]));
  const slotDayByDate = new Map(cells.map((c) => [c.calendarDate, c.slotDay]));

  const projectArtifact = (a: StudyExportArtifact) => ({
    kind: a.kind,
    partNumber: a.partNumber,
    contentType: a.contentType,
    uploadState: a.uploadState,
    sizeBytes: a.sizeBytes,
    contentHash: a.contentHash,
    objectKey: a.objectKey,
    createdAt: a.createdAt,
  });

  // -- one signed session's dataset-shaped content ----------------------------
  const projectExercises = (detail: SealedSessionDetail, week: number | null) => {
    const tweakByCode = new Map(detail.tweaks.map((t) => [t.exerciseCode, t]));
    const outcomeByCode = new Map(detail.outcomes.map((o) => [o.exerciseCode, o]));
    const planned = template.exercises.filter((e) => !tweakByCode.get(e.code)?.paused);
    const extraCodes = detail.outcomes
      .map((o) => o.exerciseCode)
      .filter((code) => !planned.some((e) => e.code === code));
    const codes = [...planned.map((e) => e.code), ...extraCodes];
    return codes.map((code) => {
      const spec = exerciseByCode.get(code) ?? null;
      const tweak = tweakByCode.get(code) ?? null;
      const outcome = outcomeByCode.get(code) ?? null;
      const confirmed = outcome?.confirmedValues ?? {};
      return {
        code,
        name: spec?.name ?? null,
        plannedSets: tweak?.sets ?? spec?.sets ?? null,
        plannedReps: tweak?.reps ?? spec?.reps ?? null,
        holdTargetSec: week === null ? null : holdSecondsOf(week, code),
        annotationMode: outcome?.annotationMode ?? null,
        status: outcome?.outcome ?? 'unanswered',
        skipReason: gate(outcome?.reason),
        performedSets: numberOrNull(confirmed['setsCompleted']?.value),
        repsPerSet: null,
        validReps: numberOrNull(confirmed['repsLogged']?.value),
        holdAchievedSec: null,
        painEvent: null,
        metric: null,
        record: outcome
          ? {
              outcomeProvenance: outcome.outcomeProvenance,
              confirmedValues: Object.fromEntries(
                Object.entries(confirmed).map(([k, v]) => [
                  k,
                  {
                    value: gateConfirmedScalar(profile, v.value),
                    provenance: v.provenance,
                    sourceTapIds: v.sourceTapIds,
                  },
                ]),
              ),
              ...(rhythmic.has(code) ? { holdApplication: 'rhythmic-no-hold' } : {}),
            }
          : null,
      };
    });
  };

  const projectSignedSession = (
    sessionId: string,
    week: number | null,
  ): {
    content: Record<string, unknown>;
    record: Record<string, unknown>;
  } | null => {
    const detail = inputs.sealedDetails[sessionId];
    const row = inputs.sealedSessions.find((s) => s.sessionId === sessionId);
    if (!detail || !row) return null;
    const obs = detail.observation;
    const exercises = projectExercises(detail, week);
    const durationMin =
      detail.startedAt && detail.endedAt
        ? Math.max(
            0,
            Math.round(
              (new Date(detail.endedAt).getTime() - new Date(detail.startedAt).getTime()) / 60_000,
            ),
          )
        : null;
    const tweakSummary =
      detail.tweaks.length === 0
        ? null
        : detail.tweaks
            .map((t) =>
              t.paused
                ? `${t.exerciseCode} paused`
                : `${t.exerciseCode} ${t.sets ?? '·'}×${t.reps ?? '·'}`,
            )
            .join(' · ');
    return {
      content: {
        status: row.endDisposition === 'early-stop-safety' ? 'completed-early-stop' : 'completed',
        exercises,
        painNRS: obs?.painNrs ?? null,
        swellingCm: obs?.swellingGirthCm ?? null,
        kneeFlexionDeg: obs?.flexionDeg ?? null,
        extensionDeficitDeg: obs?.extensionDeficitDeg ?? null,
        slrHeightDeg: obs?.slrHeightDeg ?? null,
        slrKneeLagDeg: obs?.slrKneeLagDeg ?? null,
        exercisesCompleted: detail.outcomes.filter((o) => o.outcome === 'completed').length,
        physioNote: gate(detail.signOff.sessionNote),
        preSessionTweak: tweakSummary,
        durationMin,
        startTime: detail.startedAt ? istClockOf(detail.startedAt) : null,
        observationProvenance: obs ? 'physio-entered' : null,
      },
      record: {
        sessionId,
        signedBy: who(detail.signOff.signedOffBy),
        sealedAt: detail.signOff.occurredAt,
        startedAt: detail.startedAt,
        endedAt: detail.endedAt,
        endDisposition: row.endDisposition,
        endDispositionReason: gate(row.endDispositionReason),
        observationNote: gate(obs?.note),
        assessmentAdministered: row.assessmentAdministered,
        tweaks: detail.tweaks.map((t) => ({
          exerciseCode: t.exerciseCode,
          paused: t.paused,
          sets: t.sets,
          reps: t.reps,
          rationale: gate(t.rationale),
        })),
        artifacts: detail.artifacts.map(projectArtifact),
      },
    };
  };

  // -- sessions: the 36 slots, verbatim from the cells ------------------------
  const emptySessionContent = {
    exercises: [],
    painNRS: null,
    swellingCm: null,
    kneeFlexionDeg: null,
    extensionDeficitDeg: null,
    slrHeightDeg: null,
    slrKneeLagDeg: null,
    exercisesCompleted: null,
    physioNote: null,
    preSessionTweak: null,
    durationMin: null,
    startTime: null,
    observationProvenance: null,
  };

  const sessions = cells.map((cell) => {
    const templateAssessment = assessmentByDay.get(cell.slotDay) ?? null;
    const base = {
      day: cell.slotDay,
      week: cell.week,
      dayOfWeek: cell.dayOfWeek,
      index: `Wk ${cell.week} – D${cell.dayOfWeek}`,
      date: cell.calendarDate,
      weekday: weekdayOf(cell.calendarDate),
      phase: phaseOfWeek(cell.week),
      holdTargetSec: holdSecondsOf(cell.week, null),
      isAssessmentDay: cell.isAssessment,
      assessmentTimepoint: templateAssessment?.timepoint ?? null,
      attended: cell.attended,
      sessionValid: cell.validity === 'valid' ? true : cell.validity === 'invalid' ? false : null,
      missReason: null,
      repCompliancePct: cell.repCompliancePercent,
      flags: cell.flagCodes,
    };
    if (cell.signedOff && cell.sessionId) {
      const projected = projectSignedSession(cell.sessionId, cell.week);
      if (projected) {
        return { ...base, ...emptySessionContent, ...projected.content, record: projected.record };
      }
    }
    if (cell.missed) {
      return {
        ...base,
        status: 'missed',
        ...emptySessionContent,
        exercisesCompleted: 0,
        record: null,
      };
    }
    if (cell.sessionId && !cell.signedOff) {
      // The cell's third state (ended · awaiting sign-off). The STATUS is the
      // dashboard's own public fact; the content stays absent — unsigned
      // feeds nothing, not even here (D4).
      return {
        ...base,
        status: 'awaiting-sign-off',
        ...emptySessionContent,
        record: { sessionId: cell.sessionId },
      };
    }
    return { ...base, status: 'scheduled', ...emptySessionContent, record: null };
  });

  // -- ad-hoc sealed sessions: additive home (the renderer's array stays slots) --
  const slotSessionIds = new Set(cells.map((c) => c.sessionId).filter(Boolean));
  const additionalSessions = bySealedAt
    .filter((row) => row.slot === null && !slotSessionIds.has(row.sessionId))
    .map((row) => {
      const projected = projectSignedSession(row.sessionId, null);
      const date = istDateOf(row.sealedAt);
      return {
        day: null,
        week: null,
        dayOfWeek: null,
        index: 'Ad-hoc',
        date,
        weekday: weekdayOf(date),
        phase: null,
        holdTargetSec: null,
        isAssessmentDay: false,
        assessmentTimepoint: null,
        attended: false,
        sessionValid: null,
        missReason: null,
        repCompliancePct: null,
        flags: [],
        ...emptySessionContent,
        ...(projected?.content ?? { status: 'completed' }),
        record: projected?.record ?? { sessionId: row.sessionId },
      };
    });

  // -- assessments --------------------------------------------------------------
  const assessments = [...inputs.assessments]
    .sort((a, b) => a.timepointDay - b.timepointDay)
    .map((r) => {
      const templateAssessment = assessmentByDay.get(r.timepointDay) ?? null;
      const womac =
        r.womacResponses && r.derivedWomac
          ? (() => {
              const bySubscale = (name: 'pain' | 'stiffness' | 'function') => {
                const rows = r
                  .womacResponses!.filter((w) => w.subscale === name)
                  .sort((a, b) => a.itemIndex - b.itemIndex);
                const derived = r.derivedWomac![name];
                return {
                  items: rows.map((w) => w.response),
                  score: derived.score,
                  max: derived.max,
                  itemLabels: rows.map((w) => w.label),
                  sayLabels: null,
                };
              };
              return {
                day: r.timepointDay,
                scale: `WOMAC (${r.womacResponses!.length} items, 0–4)`,
                enteredBy: who(r.actor),
                context: null,
                pain: bySubscale('pain'),
                stiffness: bySubscale('stiffness'),
                function: bySubscale('function'),
                total: r.derivedWomac!.total,
                totalMax: r.derivedWomac!.totalMax,
                normalized100: r.derivedWomac!.normalized100,
                sayLabelsNote: null,
              };
            })()
          : null;
      return {
        day: r.timepointDay,
        date: istDateOf(r.occurredAt),
        timepoint: r.derivedTarget?.timepoint ?? templateAssessment?.timepoint ?? null,
        target: r.derivedTarget?.targetText ?? null,
        targetMet: r.derivedTarget?.targetMet ?? null,
        tugSec: r.tug?.seconds ?? null,
        tugAid: r.tug?.aid ?? null,
        kneeFlexionDeg: r.kneeRom?.flexionDeg ?? null,
        extensionDeficitDeg: r.kneeRom?.extensionDeficitDeg ?? null,
        slr40: r.slr40
          ? {
              achievedDeg: r.slr40.achievedDeg,
              kneeLagDeg: r.slr40.kneeLagDeg,
              holdSec: r.slr40.holdSec,
              comfortable: r.slr40.comfortable,
              note: gate(r.slr40.note),
            }
          : null,
        womac,
        clinicalSummary: gate(r.clinicalSummary),
        provenance: 'physio-entered',
        record: {
          id: r.id,
          administeredInSessionId: r.administeredInSessionId,
          cameraReferenceVisible: r.cameraReferenceVisible,
          occurredAt: r.occurredAt,
          recordedAt: r.recordedAt,
          enteredBy: who(r.actor),
          womacBand: r.derivedWomac?.band ?? null,
          targetDetail: r.derivedTarget
            ? {
                detail: r.derivedTarget.detail,
                comparisonRuleProposed: r.derivedTarget.comparisonRuleProposed,
              }
            : null,
          tugAidNote: gate(r.tug?.aidNote),
          tugTherapistComment: gate(r.tug?.therapistComment),
        },
      };
    });

  // -- flag events ----------------------------------------------------------------
  const flagEvents = [...inputs.flags]
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .map((f) => {
      const def = flagDefByCode.get(f.code) ?? null;
      const values = Object.entries(f.evidence.values ?? {})
        .filter(([, v]) => typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean')
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(', ');
      const trigger =
        f.raisedBy === 'clinician'
          ? `${def?.label ?? f.code} — raised by physiotherapist`
          : `${def?.label ?? f.code}${values ? ` — ${values}` : ''}${
              f.evidence.thresholdsProposed ? ' [thresholds PROPOSED]' : ''
            }`;
      return {
        day: slotDayByDate.get(istDateOf(f.occurredAt)) ?? null,
        code: f.code,
        severity: f.severity,
        trigger,
        action: gate(f.resolution?.action),
        resolvedDay: f.resolution
          ? (slotDayByDate.get(istDateOf(f.resolution.occurredAt)) ?? null)
          : null,
        resolution: gate(f.resolution?.resolution),
        record: {
          id: f.id,
          raisedBy: f.raisedBy,
          actor: who(f.actor),
          occurredAt: f.occurredAt,
          recordedAt: f.recordedAt,
          ruleVersion: f.ruleVersion,
          note: gate(f.note),
          evidence: {
            kind: f.evidence.kind,
            refs: {
              sessionId: f.evidence.refs.sessionId ?? null,
              assessmentId: f.evidence.refs.assessmentId ?? null,
              observationId: f.evidence.refs.observationId ?? null,
            },
            values: f.evidence.values,
            thresholdsProposed: f.evidence.thresholdsProposed,
          },
          open: f.resolution === null,
          resolution: f.resolution
            ? {
                occurredAt: f.resolution.occurredAt,
                recordedAt: f.resolution.recordedAt,
                actor: who(f.resolution.actor),
              }
            : null,
        },
      };
    });

  // -- compliance -------------------------------------------------------------------
  const elapsedCells = cells.filter((c) => c.calendarDate <= inputs.derivedAgainstDate);
  let running = 0;
  const cumulativeAdherence = elapsedCells.map((c, i) => {
    if (c.attended) running += 1;
    return { day: c.slotDay, cumulativeAdherencePct: round1((running / (i + 1)) * 100) };
  });

  const complianceBlock = {
    sessionsPlanned: compliance.scheduledTotal,
    sessionsAttended: compliance.attended,
    sessionsMissed: compliance.missedSlots,
    sessionAdherencePct: compliance.adherencePercent,
    validSessions: compliance.sessions.valid,
    exerciseInstancesPlanned: compliance.exerciseInstances.planned,
    exerciseInstancesCompleted: compliance.exerciseInstances.completed,
    exerciseInstancesPartial: compliance.exerciseInstances.partial,
    exerciseInstancesSkipped: compliance.exerciseInstances.skipped,
    repLevel: {
      plannedValidReps: compliance.repLevel.derivablePlannedReps,
      validReps: compliance.repLevel.countedReps,
      repCompliancePct: compliance.repLevel.repCompliancePercent,
    },
    adherenceFlagThresholdPct: compliance.adherenceFloorPercent,
    adherenceFlagTriggered: inputs.flags.some((f) => f.code === 'ADHER_70'),
    cumulativeAdherence,
    note: null,
    record: {
      thresholdsProposed: compliance.thresholdsProposed,
      scheduledElapsed: compliance.scheduledElapsed,
      adherenceBelowFloorToday: compliance.adherenceBelowFloor,
      sessionsSigned: compliance.sessions,
      exerciseInstancesUnanswered: compliance.exerciseInstances.unanswered,
      repLevelDetail: {
        plannedRepsTotal: compliance.repLevel.plannedReps,
        exercisesUnknown: compliance.repLevel.exercisesUnknown,
      },
      perExercise: compliance.perExercise,
    },
  };

  // -- protocol + library, projected from the pinned template ------------------------
  const protocol = {
    id: `${template.id}@${template.version}`,
    title: template.title,
    procedure: null,
    status: template.status,
    recoveryWindow: null,
    delivery: null,
    sessionAnchor: null,
    governingRule: null,
    // The document's "Measurement & Validity Rules", verbatim from the PINNED
    // template — so an old episode exports the rules IT ran under, not today's.
    validityRules: [...template.validityRules],
    phases: template.phases.map((p) => ({
      phase: p.number,
      name: p.name,
      weeks: [p.fromWeek, p.toWeek] as [number, number],
      goal: p.goal,
    })),
    holdProgression: Object.fromEntries(
      template.holdProgression.map((h) => [
        String(h.week),
        h.holdSecondsMin === h.holdSecondsMax
          ? `${h.holdSecondsMin} s`
          : `${h.holdSecondsMin}–${h.holdSecondsMax} s`,
      ]),
    ),
    holdSeconds: Object.fromEntries(
      template.holdProgression.map((h) => [
        String(h.week),
        h.holdSecondsMin === h.holdSecondsMax ? h.holdSecondsMin : null,
      ]),
    ),
    flags: inputs.flagDefinitions.map((d) => ({
      code: d.code,
      label: d.label,
      definition: d.ruleText,
      severity: d.severity,
      mode: d.mode,
      thresholdsProposed: d.thresholdsProposed,
    })),
    // `kind` → `type` is the ONE rename here, and it is deliberate: the
    // reference dataset names this key `type`, and this projection's whole job
    // is to match that shape. The template keeps `kind` because that is our
    // clinical model's word. Anyone changing either side changes both.
    safetyBoundaries: template.safetyBoundaries.map((b) => ({
      rule: b.rule,
      type: b.kind,
    })),
    assessmentSchedule: template.assessments.map((a) => ({
      day: a.day,
      timepoint: a.timepoint,
      instruments: [...a.instruments],
      target: a.targetText,
    })),
  };

  const exerciseLibrary = template.exercises.map((e) => ({
    code: e.code,
    name: e.name,
    focus: e.focus,
    measured: e.movement,
    position: null,
    metricKey: null,
    metricLabel: null,
    unit: null,
    plannedSets: e.sets,
    plannedReps: e.reps,
  }));

  // -- patient: the explicit allowlist. name/surgeon/facility/supportPerson are
  // NEVER read — identity is the study code (delta 2), third parties are
  // withheld, the physiotherapist is a pseudonym (call 5).
  //
  // `diagnosis` and `procedure` are FREE TEXT — enrolment types them as 500-
  // and 300-character strings (episode.ts) — so they pass through the gate
  // like every other physio-authored sentence. Until increment 12's audit
  // (AUD-16, 2026-09-08) both were copied ungated: a pii-redacted file whose
  // cover note said "no unscreened sentence travels" carried "referred by
  // Dr Kulkarni" in this block. Neither field was in the header's free-text
  // inventory, which is how a gate that exists still gets walked around —
  // the allowlist names WHAT travels, the gate decides HOW, and a string
  // field needs both.
  //
  // `comorbidities` is NOT gated, deliberately: increment 9 recorded it as a
  // kept quasi-identifier ("because the report renders them" — the plan's
  // redaction-surface line), and reversing a recorded call is the owner's
  // (register AUD-05, NEEDS-OWNER-RULING). Gate it here only on that ruling.
  const bmi =
    inputs.patient.heightCm != null &&
    inputs.patient.weightKg != null &&
    inputs.patient.heightCm > 0
      ? round1(inputs.patient.weightKg / (inputs.patient.heightCm / 100) ** 2)
      : null;
  const postOpDayAtStart =
    inputs.episode.rehabStartDate != null
      ? Math.round(
          (new Date(`${inputs.episode.rehabStartDate}T00:00:00Z`).getTime() -
            new Date(`${inputs.episode.surgeryDate}T00:00:00Z`).getTime()) /
            86_400_000,
        )
      : null;
  const patient = {
    id: inputs.studyCode,
    synthetic: false,
    name: inputs.studyCode,
    age: inputs.patient.ageYears,
    sex:
      inputs.patient.sex === 'female'
        ? 'F'
        : inputs.patient.sex === 'male'
          ? 'M'
          : inputs.patient.sex === 'other'
            ? 'O'
            : null,
    heightCm: inputs.patient.heightCm,
    weightKg: inputs.patient.weightKg,
    bmi,
    diagnosis: gate(inputs.patient.diagnosis),
    procedure: gate(inputs.episode.procedure),
    operatedSide: inputs.episode.operatedSide,
    surgeryDate: inputs.episode.surgeryDate,
    rehabStartDate: inputs.episode.rehabStartDate,
    postOpDayAtStart,
    comorbidities: [...inputs.patient.comorbidities],
    surgeon: null,
    physiotherapist: who(inputs.episode.createdBy),
    facility: null,
    supportPerson: null,
    setting: null,
  };

  // -- meta ---------------------------------------------------------------------------
  const meta = {
    title: `AdvaitNetra study export — ${inputs.studyCode}`,
    generated: istDateOf(inputs.exportedAt),
    // The disclaimer is the file's own cover note, and the two profiles get
    // DIFFERENT ones because they are different kinds of document. Only the
    // first may call itself redacted (call 4).
    disclaimer:
      (freeTextIncluded
        ? `⚠ Real clinical episode — NOT PII-REDACTED (profile: ${profile}). ` +
          `Structured identifiers are removed: the patient appears only as a study code, ` +
          `third-party name FIELDS (support person, surgeon, facility) are withheld, and ` +
          `clinician identity is a stable pseudonym. BUT PHYSIO FREE TEXT IS INCLUDED VERBATIM ` +
          `AND UNSCREENED — session notes, clinical summaries, plan-tweak rationales, ` +
          `early-stop reasons, observation notes, flag actions/resolutions, and the ` +
          `diagnosis and procedure as typed at enrolment MAY CONTAIN the ` +
          `patient's own name, a family member's name, or a clinician's name, because no rule ` +
          `can safely redact a name out of prose. THIS FILE MUST BE READ BY A HUMAN AND ` +
          `SCREENED BEFORE IT LEAVES THE BUILDING. `
        : `Real clinical episode, PII-redacted (profile: ${profile}). ` +
          `The patient appears only as a study code; third-party names (support person, surgeon, ` +
          `facility) are withheld; clinician identity is a stable pseudonym; and physio free text ` +
          `— including the diagnosis and procedure as typed at enrolment — is EXCLUDED, so no ` +
          `unscreened sentence travels. `) +
      `Media bytes are excluded — the artifact index travels instead. ` +
      `Protocol content status: ${template.status}. ` +
      `Flag thresholds and the target comparison rule are PROPOSED pending clinical ratification (OQ4). ` +
      `Retention follows the policy version pinned on the episode's consent row: AN-RETENTION@0.1 ` +
      `(the DRAFT consent's) is PROPOSED and never purges without acknowledgement; AN-RETENTION@1.0 ` +
      `(AN-TKR-consent@1.0) is RATIFIED at five years from the last session (ADR-0029).`,
    protocolRef: `${template.id}@${template.version} — ${template.title} (${template.status}); transcribed from: ${template.source}`,
    dataHierarchy: [
      `Protocol (pinned ${template.id}@${template.version}) → Week (${template.weeks}) → Slot (${template.weeks * template.daysPerWeek}) → Sealed session → Exercise outcome → Confirmed values`,
      'Two streams: DAILY (physio-confirmed session records — sealed sign-offs only) and FORMAL ASSESSMENT (physio-entered instruments at protocol timepoints).',
      'Unsigned sessions feed nothing here: absence renders as absent, never as zero.',
    ],
    provenance: {
      scheme:
        'capture ≠ input: every clinical value is physiotherapist-entered (own tap / typed entry / confirmed edit), with per-value provenance and source tap ids on the record',
      sessionObservations: 'physio-entered typed panel; a skipped field is null, never zero',
      tapDerived:
        'confirmed session values derive from the physiotherapist’s own live taps or typed entry; the provenance travels per value',
      exerciseMetrics:
        'not captured: camera reference context is never filed as a clinical value and does not appear in this export',
      assessments: 'physiotherapist-administered and physiotherapist-entered',
    },
    platformRecord: {
      episodeId: inputs.episode.id,
      studyCode: inputs.studyCode,
      exportedAt: inputs.exportedAt,
      derivedAgainstDate: inputs.derivedAgainstDate,
      redactionProfile: profile,
      freeTextIncluded,
      schemaVersion: STUDY_EXPORT_SCHEMA_VERSION,
      protocolVersion: inputs.episode.protocolVersion,
      consentTextVersion: inputs.episode.consentTextVersion,
      recordingConsented: inputs.episode.recordingConsented,
      additionalSessions,
      assessmentMissedEvents: inputs.assessmentMissedEvents.map((m) => ({
        timepointDay: m.timepointDay,
        reason: m.reason,
        note: gate(m.note),
        sessionId: m.sessionId,
        occurredAt: m.occurredAt,
        recordedAt: m.recordedAt,
        actor: who(m.actor),
      })),
      consentDocumentArtifacts: inputs.consentDocumentArtifacts.map(projectArtifact),
    },
  };

  return studyExportSchema.parse({
    meta,
    protocol,
    exerciseLibrary,
    patient,
    sessions,
    assessments,
    flagEvents,
    compliance: complianceBlock,
    day36Trace: null,
  });
}

// ---- the wire schema (the contract, strict everywhere) --------------------------

export const STUDY_EXPORT_SCHEMA_VERSION = '1.0' as const;

const artifactIndexEntrySchema = z
  .object({
    kind: z.string(),
    partNumber: z.number().int(),
    contentType: z.string(),
    uploadState: z.string(),
    sizeBytes: z.number().nullable(),
    contentHash: z.string().nullable(),
    objectKey: z.string(),
    createdAt: z.iso.datetime(),
  })
  .strict();

const exportExerciseSchema = z
  .object({
    code: z.string(),
    name: z.string().nullable(),
    plannedSets: z.number().int().nullable(),
    plannedReps: z.number().int().nullable(),
    holdTargetSec: z.number().nullable(),
    annotationMode: z.string().nullable(),
    status: z.enum(['completed', 'partial', 'skipped', 'unanswered']),
    skipReason: z.string().nullable(),
    performedSets: z.number().nullable(),
    repsPerSet: z.null(),
    validReps: z.number().nullable(),
    holdAchievedSec: z.null(),
    painEvent: z.null(),
    metric: z.null(),
    record: z
      .object({
        outcomeProvenance: z.string(),
        confirmedValues: z.record(
          z.string(),
          z
            .object({
              value: z.union([z.number(), z.string(), z.boolean()]).nullable(),
              provenance: z.string(),
              sourceTapIds: z.array(z.uuid()),
            })
            .strict(),
        ),
        holdApplication: z.string().optional(),
      })
      .strict()
      .nullable(),
  })
  .strict();

const exportSessionRecordSchema = z
  .object({
    sessionId: z.uuid(),
    signedBy: z
      .string()
      .regex(/^Clinician-[A-Z]+$/)
      .optional(),
    sealedAt: z.iso.datetime().optional(),
    startedAt: z.iso.datetime().nullable().optional(),
    endedAt: z.iso.datetime().nullable().optional(),
    endDisposition: z.string().nullable().optional(),
    endDispositionReason: z.string().nullable().optional(),
    observationNote: z.string().nullable().optional(),
    assessmentAdministered: z.boolean().optional(),
    tweaks: z
      .array(
        z
          .object({
            exerciseCode: z.string(),
            paused: z.boolean(),
            sets: z.number().nullable(),
            reps: z.number().nullable(),
            rationale: z.string().nullable(),
          })
          .strict(),
      )
      .optional(),
    artifacts: z.array(artifactIndexEntrySchema).optional(),
  })
  .strict();

const exportSessionSchema = z
  .object({
    day: z.number().int().nullable(),
    week: z.number().int().nullable(),
    dayOfWeek: z.number().int().nullable(),
    index: z.string(),
    date: z.iso.date(),
    weekday: z.string(),
    phase: z.number().int().nullable(),
    holdTargetSec: z.number().nullable(),
    isAssessmentDay: z.boolean(),
    assessmentTimepoint: z.string().nullable(),
    status: z.enum([
      'completed',
      'completed-early-stop',
      'missed',
      'awaiting-sign-off',
      'scheduled',
    ]),
    attended: z.boolean(),
    sessionValid: z.boolean().nullable(),
    missReason: z.null(),
    exercises: z.array(exportExerciseSchema),
    painNRS: z.number().nullable(),
    swellingCm: z.number().nullable(),
    kneeFlexionDeg: z.number().nullable(),
    extensionDeficitDeg: z.number().nullable(),
    slrHeightDeg: z.number().nullable(),
    slrKneeLagDeg: z.number().nullable(),
    repCompliancePct: z.number().nullable(),
    exercisesCompleted: z.number().int().nullable(),
    flags: z.array(z.string()),
    physioNote: z.string().nullable(),
    preSessionTweak: z.string().nullable(),
    durationMin: z.number().nullable(),
    startTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .nullable(),
    observationProvenance: z.string().nullable(),
    record: exportSessionRecordSchema.nullable(),
  })
  .strict();

const womacSubscaleBlockSchema = z
  .object({
    items: z.array(z.number().int().min(0).max(4)),
    score: z.number(),
    max: z.number(),
    itemLabels: z.array(z.string()),
    sayLabels: z.null(),
  })
  .strict();

const exportAssessmentSchema = z
  .object({
    day: z.number().int(),
    date: z.iso.date(),
    timepoint: z.string().nullable(),
    target: z.string().nullable(),
    targetMet: z.boolean().nullable(),
    tugSec: z.number().nullable(),
    tugAid: z.string().nullable(),
    kneeFlexionDeg: z.number().nullable(),
    extensionDeficitDeg: z.number().nullable(),
    slr40: z
      .object({
        achievedDeg: z.number(),
        kneeLagDeg: z.number(),
        holdSec: z.number(),
        comfortable: z.boolean(),
        note: z.string().nullable(),
      })
      .strict()
      .nullable(),
    womac: z
      .object({
        day: z.number().int(),
        scale: z.string(),
        enteredBy: z.string().regex(/^Clinician-[A-Z]+$/),
        context: z.null(),
        pain: womacSubscaleBlockSchema,
        stiffness: womacSubscaleBlockSchema,
        function: womacSubscaleBlockSchema,
        total: z.number(),
        totalMax: z.number(),
        normalized100: z.number(),
        sayLabelsNote: z.null(),
      })
      .strict()
      .nullable(),
    clinicalSummary: z.string().nullable(),
    provenance: z.string(),
    record: z
      .object({
        id: z.uuid(),
        administeredInSessionId: z.uuid().nullable(),
        cameraReferenceVisible: z.boolean(),
        occurredAt: z.iso.datetime(),
        recordedAt: z.iso.datetime(),
        enteredBy: z.string().regex(/^Clinician-[A-Z]+$/),
        womacBand: z.object({ label: z.string(), description: z.string() }).strict().nullable(),
        // Per-clause target verdicts, exactly as the assessment read serves
        // them (derivedTargetSchema): the clause TEXT verbatim, the measured
        // value with its unit, and the tri-state met. `text` is the authored
        // clause, so it is clinical content arriving as data — never authored
        // here. ⚠ This block is only NON-EMPTY at a timepoint that authors a
        // target: baseline authors none, which is why the first cut of this
        // schema (a `clause` object) passed every test and failed on the
        // first Day-15 episode anyone generated.
        targetDetail: z
          .object({
            detail: z.array(
              z
                .object({
                  text: z.string(),
                  value: z.string().nullable(),
                  met: z.boolean().nullable(),
                })
                .strict(),
            ),
            comparisonRuleProposed: z.literal(true),
          })
          .strict()
          .nullable(),
        tugAidNote: z.string().nullable(),
        tugTherapistComment: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

const exportFlagEventSchema = z
  .object({
    day: z.number().int().nullable(),
    code: z.string(),
    severity: z.string(),
    trigger: z.string(),
    action: z.string().nullable(),
    resolvedDay: z.number().int().nullable(),
    resolution: z.string().nullable(),
    record: z
      .object({
        id: z.string(),
        raisedBy: z.string(),
        actor: z.string().regex(/^Clinician-[A-Z]+$/),
        occurredAt: z.string(),
        recordedAt: z.string(),
        ruleVersion: z.string(),
        note: z.string().nullable(),
        evidence: z
          .object({
            kind: z.string(),
            refs: z
              .object({
                sessionId: z.string().nullable(),
                assessmentId: z.string().nullable(),
                observationId: z.string().nullable(),
              })
              .strict(),
            values: z.record(z.string(), z.unknown()),
            thresholdsProposed: z.boolean(),
          })
          .strict(),
        open: z.boolean(),
        resolution: z
          .object({
            occurredAt: z.string(),
            recordedAt: z.string(),
            actor: z.string().regex(/^Clinician-[A-Z]+$/),
          })
          .strict()
          .nullable(),
      })
      .strict(),
  })
  .strict();

export const studyExportSchema = z
  .object({
    meta: z
      .object({
        title: z.string(),
        generated: z.iso.date(),
        disclaimer: z.string(),
        protocolRef: z.string(),
        dataHierarchy: z.array(z.string()).length(3),
        provenance: z
          .object({
            scheme: z.string(),
            sessionObservations: z.string(),
            tapDerived: z.string(),
            exerciseMetrics: z.string(),
            assessments: z.string(),
          })
          .strict(),
        platformRecord: z
          .object({
            episodeId: z.uuid(),
            studyCode: z.string().regex(/^Patient-\d{3,}$/),
            exportedAt: z.iso.datetime(),
            derivedAgainstDate: z.iso.date(),
            redactionProfile: studyExportProfileSchema,
            freeTextIncluded: z.boolean(),
            schemaVersion: z.literal(STUDY_EXPORT_SCHEMA_VERSION),
            protocolVersion: z.string(),
            consentTextVersion: z.string(),
            recordingConsented: z.boolean(),
            additionalSessions: z.array(exportSessionSchema),
            assessmentMissedEvents: z.array(
              z
                .object({
                  timepointDay: z.number().int(),
                  reason: z.string(),
                  note: z.string().nullable(),
                  sessionId: z.uuid().nullable(),
                  occurredAt: z.iso.datetime(),
                  recordedAt: z.iso.datetime(),
                  actor: z.string().regex(/^Clinician-[A-Z]+$/),
                })
                .strict(),
            ),
            consentDocumentArtifacts: z.array(artifactIndexEntrySchema),
          })
          .strict(),
      })
      .strict(),
    protocol: z
      .object({
        id: z.string(),
        title: z.string(),
        procedure: z.null(),
        status: z.string(),
        recoveryWindow: z.null(),
        delivery: z.null(),
        sessionAnchor: z.null(),
        governingRule: z.null(),
        validityRules: z.array(z.string().min(1)).min(1),
        phases: z.array(
          z
            .object({
              phase: z.number().int(),
              name: z.string(),
              weeks: z.tuple([z.number().int(), z.number().int()]),
              goal: z.string(),
            })
            .strict(),
        ),
        holdProgression: z.record(z.string(), z.string()),
        holdSeconds: z.record(z.string(), z.number().nullable()),
        flags: z.array(
          z
            .object({
              code: z.string(),
              label: z.string(),
              definition: z.string(),
              severity: z.string(),
              mode: z.string(),
              thresholdsProposed: z.boolean(),
            })
            .strict(),
        ),
        safetyBoundaries: z
          .array(
            z
              .object({
                rule: z.string().min(1),
                type: z.enum(['stop', 'technique', 'escalate']),
              })
              .strict(),
          )
          .min(1),
        assessmentSchedule: z.array(
          z
            .object({
              day: z.number().int(),
              timepoint: z.string(),
              instruments: z.array(z.string()),
              target: z.string().nullable(),
            })
            .strict(),
        ),
      })
      .strict(),
    exerciseLibrary: z.array(
      z
        .object({
          code: z.string(),
          name: z.string(),
          focus: z.string(),
          measured: z.string(),
          position: z.null(),
          metricKey: z.null(),
          metricLabel: z.null(),
          unit: z.null(),
          plannedSets: z.number().int(),
          plannedReps: z.number().int(),
        })
        .strict(),
    ),
    patient: z
      .object({
        id: z.string().regex(/^Patient-\d{3,}$/),
        synthetic: z.literal(false),
        name: z.string().regex(/^Patient-\d{3,}$/),
        age: z.number().nullable(),
        sex: z.enum(['F', 'M', 'O']).nullable(),
        heightCm: z.number().nullable(),
        weightKg: z.number().nullable(),
        bmi: z.number().nullable(),
        diagnosis: z.string().nullable(),
        procedure: z.string().nullable(),
        operatedSide: z.enum(['left', 'right']),
        surgeryDate: z.iso.date(),
        rehabStartDate: z.iso.date().nullable(),
        postOpDayAtStart: z.number().int().nullable(),
        comorbidities: z.array(z.string()),
        surgeon: z.null(),
        physiotherapist: z.string().regex(/^Clinician-[A-Z]+$/),
        facility: z.null(),
        supportPerson: z.null(),
        setting: z.null(),
      })
      .strict(),
    sessions: z.array(exportSessionSchema),
    assessments: z.array(exportAssessmentSchema),
    flagEvents: z.array(exportFlagEventSchema),
    compliance: z
      .object({
        sessionsPlanned: z.number().int(),
        sessionsAttended: z.number().int(),
        sessionsMissed: z.number().int(),
        sessionAdherencePct: z.number().nullable(),
        validSessions: z.number().int(),
        exerciseInstancesPlanned: z.number().int(),
        exerciseInstancesCompleted: z.number().int(),
        exerciseInstancesPartial: z.number().int(),
        exerciseInstancesSkipped: z.number().int(),
        repLevel: z
          .object({
            plannedValidReps: z.number().int(),
            validReps: z.number().nullable(),
            repCompliancePct: z.number().nullable(),
          })
          .strict(),
        adherenceFlagThresholdPct: z.number(),
        adherenceFlagTriggered: z.boolean(),
        cumulativeAdherence: z.array(
          z.object({ day: z.number().int(), cumulativeAdherencePct: z.number() }).strict(),
        ),
        note: z.null(),
        record: z
          .object({
            thresholdsProposed: z.literal(true),
            scheduledElapsed: z.number().int(),
            adherenceBelowFloorToday: z.boolean(),
            sessionsSigned: z
              .object({
                signedTotal: z.number().int(),
                valid: z.number().int(),
                invalid: z.number().int(),
                notDerivable: z.number().int(),
              })
              .strict(),
            exerciseInstancesUnanswered: z.number().int(),
            repLevelDetail: z
              .object({
                plannedRepsTotal: z.number().int(),
                exercisesUnknown: z.number().int(),
              })
              .strict(),
            perExercise: z.array(
              z
                .object({
                  exerciseCode: z.string(),
                  plannedInstances: z.number().int(),
                  completed: z.number().int(),
                  partial: z.number().int(),
                  skipped: z.number().int(),
                  unanswered: z.number().int(),
                  confirmedSets: z.number().int(),
                  confirmedReps: z.number().int(),
                })
                .strict(),
            ),
          })
          .strict(),
      })
      .strict(),
    day36Trace: z.null(),
  })
  .strict();
export type StudyExport = z.infer<typeof studyExportSchema>;
